import Button from '../Button'
import { useI18n } from '../../i18n/I18nProvider'
import { useMemo } from 'react'

function PlantCard({ plant, onDestroy, onCollectSeed }) {
  const { t } = useI18n()
  const totalClicks = plant.config.clicksPerStage * plant.config.stages
  const isMature = plant.progress >= totalClicks
  const progressPercent = (plant.stage / (plant.config.stages - 1)) * 100
  
  // Fonction pour obtenir le nom traduit de la plante
  const getTranslatedPlantName = useMemo(() => {
    if (!plant) return t('plants.default.name')
    
    // Essayer d'abord avec la traduction spécifique au type de plante
    const plantType = plant.plantType || 'default'
    const translatedName = t(`plants.${plantType}.name`, { 
      defaultValue: plant?.config?.name || plantType 
    })
    
    return translatedName
  }, [plant, t])
  
  return (
    <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-bold text-lg">{getTranslatedPlantName}</p>
          <p className="text-sm text-gray-600">
            {t('plantCard.stage')} {plant.stage + 1} / {plant.config.stages}
            {isMature && ' ✅ ' + t('plantCard.mature')}
          </p>
        </div>
        <div className="flex gap-2" />
      </div>
      
      {/* Progress Bar */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>{t('plantCard.progression')}</span>
          <span>{Math.floor(progressPercent)}%</span>
        </div>
        <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className="bg-green-600 h-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
      
      {/* Seed Generation Progress (only for mature plants) */}
      {isMature && (
        <div className="mt-3 p-3 bg-white rounded-lg">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>{t('plantCard.seedProduction')}</span>
            <span>
              {Math.floor(plant.seedProgress || 0)}% ~ {(() => {
                const interval = plant.config.seedGenerationInterval || 300000
                const remaining = interval * (1 - (plant.seedProgress || 0) / 100)
                const minutes = Math.floor(remaining / 60000)
                const seconds = Math.floor((remaining % 60000) / 1000)
                return `${minutes}:${seconds.toString().padStart(2, '0')}`
              })()}
            </span>
          </div>
          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-green-500 h-full transition-all duration-300"
              style={{ width: `${plant.seedProgress || 0}%` }}
            />
          </div>
          {plant.pendingSeed && (
            <Button
              onClick={() => onCollectSeed(plant.id)}
              size="sm"
              className="w-full mt-2 animate-pulse"
            >
              {t('plantCard.collectSeed')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default PlantCard
